import * as THREE from 'three';
import type { BoundingBox, BoxProps, Matrix4, Number3, TransformTarget } from '../types';
import { type PointCoordinates } from './PointCoordinates';
import { DirectionArrow, UnitSystemEnum } from './Enums';
import { type Transform } from './VisibilityBox';

export const METERS_TO_INCHES_FACTOR = 39.37007874;
export const METERS_TO_FEET_FACTOR = 3.28084;

export const identityMatrix4: Matrix4 = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

export const UNITARY_CUBOID = new THREE.Box3(
  new THREE.Vector3(-0.5, -0.5, -0.5),
  new THREE.Vector3(0.5, 0.5, 0.5)
);

export const rgbFromRgba = (rgba: number): number => Math.trunc(rgba / 256);

export const disposeMaterials = (materials: THREE.Material | THREE.Material[]) => {
  if (Array.isArray(materials)) {
    materials.forEach((material) => {
      material.dispose();
    });
  } else {
    materials.dispose();
  }
};

export const makeNEDtoENURotationMatrix = (): THREE.Matrix4 => {
  // NED_TO_ENU_ROTATION = Rotation.from_euler("xyz", [180, 0, 90], degrees=True)
  const euler = new THREE.Euler(Math.PI, 0, Math.PI / 2, 'ZYX');
  return new THREE.Matrix4().makeRotationFromEuler(euler);
};

export const convertEnuScansTransfromToNedCadTransform = (enuScansToCADmatrix: THREE.Matrix4) => {
  // returning CAD to Scans transform in NED
  // ST is enuScansToCADmatrix
  // ST * scans_enu = cad_enu
  // scans_enu = inv(ST) * cad_enu
  // T * scans_enu = T * inv(ST) * cad_enu
  // T * scans_enu = (T * inv(ST)) * inv(T) * T * cad_enu
  // scans_ned = (T * inv(ST) * inv(T)) * cad_ned
  // scans_ned = (T * inv(ST) * T) * cad_ned
  // T is NEDtoENU
  // scans_ned = (T * inv(ST) * T) * cad_ned     (more explanation below)
  const mat4 = enuScansToCADmatrix.invert();
  const NEDtoENU = makeNEDtoENURotationMatrix();
  mat4.premultiply(NEDtoENU).multiply(NEDtoENU);
};

export const decomposeMatrix4 = (
  matrix4: THREE.Matrix4
): {
  position: THREE.Vector3;
  quat: THREE.Quaternion;
  euler: THREE.Euler;
  scale: THREE.Vector3;
} => {
  const position = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  matrix4.decompose(position, quat, scale);
  const euler = new THREE.Euler().setFromQuaternion(quat);

  return {
    position,
    quat,
    euler,
    scale,
  };
};

export const decomposeThreeMatrix4 = (matrix4: THREE.Matrix4): Transform => {
  const position = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  matrix4.decompose(position, quat, scale);
  const euler = new THREE.Euler().setFromQuaternion(quat);

  return {
    position: position.toArray(),
    rotation: euler.toArray().slice(0, 3) as Number3,
    scale: scale.toArray(),
  };
};

export const composeThreeMatrix4 = ({ position, rotation, scale }: Transform): THREE.Matrix4 => {
  return new THREE.Matrix4().compose(
    new THREE.Vector3(...position),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(...rotation)),
    new THREE.Vector3(...scale)
  );
};

export const diplayNumberArray = (values: number[], precision = 6): string[] => {
  return values.map((value: number) => value.toFixed(precision));
};

export const displayDecomposedMatrix = (matrix: THREE.Matrix4) => {
  const { position, rotation, scale } = decomposeThreeMatrix4(matrix);
  return {
    position: diplayNumberArray(position),
    rotation: diplayNumberArray(rotation),
    scale: diplayNumberArray(scale),
  };
};

export const positionRotationScaleFromMatrix4 = (
  matrix: Matrix4
): { position: Number3; rotation: Number3; scale: Number3 } => {
  return decomposeThreeMatrix4(new THREE.Matrix4().fromArray(matrix));
};

export const matrix4FromPositionRotationScale = ({
  position,
  rotation,
  scale,
}: {
  position: Number3;
  rotation: Number3;
  scale: Number3;
}): Matrix4 => {
  const positionVec = new THREE.Vector3(...position);
  const quat = new THREE.Quaternion().setFromEuler(new THREE.Euler(...rotation));
  const scaleVec = new THREE.Vector3(...scale);

  return new THREE.Matrix4().compose(positionVec, quat, scaleVec).toArray();
};

export const rotationCenterFromWorldPoint = (target: TransformTarget, point: Number3): Number3 => {
  const { leafGroup } = target;
  leafGroup.updateMatrixWorld(true);
  const rotationCenter = new THREE.Vector3(...point).applyMatrix4(
    leafGroup.matrixWorld.clone().invert()
  );
  return rotationCenter.toArray();
};

export const adjustPoseForRotationCenter = (
  pose: Transform,
  rotationCenter: Number3 | undefined
): Transform => {
  // Adjust actual pose according to non-zero rotation center
  if (!rotationCenter) {
    return pose;
  }
  const poseMat4 = composeThreeMatrix4(pose);

  // Trc - rotation center translation, Tnegrc - negative rotation center translation
  // poseMat4 = Trc * adjMat4 * Tnegrc  (these correspond to groups tree below)
  // inv(Trc) * poseMat4 * inv(Tnegrc) = adjMat4
  // assuming Tnegrc = inv(Trc)
  // Tnegrc * poseMat4 * Trc = adjMat4
  const rcMat = new THREE.Matrix4().makeTranslation(new THREE.Vector3(...rotationCenter));
  const negrcMat = new THREE.Matrix4().makeTranslation(
    new THREE.Vector3(-rotationCenter[0], -rotationCenter[1], -rotationCenter[2])
  );
  const adjMat4 = poseMat4.premultiply(negrcMat).multiply(rcMat);
  return decomposeThreeMatrix4(adjMat4);
};

export const undoAdjustPoseForRotationCenter = (
  pose: Transform,
  rotationCenter: Number3 | undefined
): Transform => {
  // Undo rotation center adjustment (adjustPoseForRotationCenter)
  if (!rotationCenter) {
    return pose;
  }
  const adjMat4 = composeThreeMatrix4(pose);

  // Trc - rotation center translation, Tnegrc - negative rotation center translation
  // poseMat4 = Trc * adjMat4 * Tnegrc  (these correspond to groups tree below)

  const rcMat = new THREE.Matrix4().makeTranslation(new THREE.Vector3(...rotationCenter));
  const negrcMat = new THREE.Matrix4().makeTranslation(
    new THREE.Vector3(-rotationCenter[0], -rotationCenter[1], -rotationCenter[2])
  );
  const poseMat4 = adjMat4.premultiply(rcMat).multiply(negrcMat);
  return decomposeThreeMatrix4(poseMat4);
};

export const alignModelByTwoVectors = (
  fromDirection: THREE.Vector3,
  toDirection: THREE.Vector3,
  transform: Matrix4
): Matrix4 => {
  const quaternion = new THREE.Quaternion();
  quaternion.setFromUnitVectors(fromDirection, toDirection);

  const matrix4 = new THREE.Matrix4();
  matrix4.makeRotationFromQuaternion(quaternion);

  const initialMatrix = new THREE.Matrix4();
  initialMatrix.fromArray(transform);
  initialMatrix.premultiply(matrix4);

  return initialMatrix.toArray();
};

export const alignModelByTwoPoints = (
  a: PointCoordinates,
  b: PointCoordinates,
  transform: Matrix4
): Matrix4 => {
  // direction from a to b
  const direction = new THREE.Vector3(b.x - a.x, b.y - a.y, b.z - a.z);
  direction.normalize();

  const zDirection = new THREE.Vector3(0, 0, 1);

  return alignModelByTwoVectors(direction, zDirection, transform);
};

// Expensive operation involves cloning of object
export const computeBoundingBox = (object: THREE.Object3D): BoundingBox => {
  const objectCopy = object.clone();

  // TODO: adjust for case if parent world transform isn't identity
  // const parentWorldInverse = object.parent ? object.parent.matrixWorld.clone().invert() : undefined;
  // console.log('parentWorldInverse', parentWorldInverse);

  objectCopy.updateMatrixWorld(true);
  objectCopy.traverse((child) => {
    // make geometry to have world coordinates
    if (child.isObject3D) {
      const mesh = child as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.geometry = mesh.geometry.clone();
        mesh.geometry.applyMatrix4(mesh.matrixWorld);
        mesh.geometry.computeBoundingBox();
      }
    }
  });
  objectCopy.traverse((child) => {
    // reset transformation matrices
    if (child.isObject3D) {
      child.position.set(0, 0, 0);
      child.rotation.set(0, 0, 0);
      child.scale.set(1, 1, 1);
      child.updateMatrix();
    }
  });
  objectCopy.updateMatrixWorld(true);

  objectCopy.traverse((child) => {
    // make geometry to have world coordinates
    if (child.isObject3D) {
      const mesh = child as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.geometry.computeBoundingBox();
      }
    }
  });
  const bbox = new THREE.Box3();
  bbox.setFromObject(objectCopy);

  objectCopy.traverse((child) => {
    // make geometry to have world coordinates
    if (child.isObject3D) {
      const mesh = child as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.geometry.dispose();
      }
    }
  });

  return { min: bbox.min.toArray(), max: bbox.max.toArray() };
};

export const getObjectBoundingBox = (object: THREE.Object3D): BoundingBox => {
  const bbox = new THREE.Box3();
  bbox.setFromObject(object);
  return { min: bbox.min.toArray(), max: bbox.max.toArray() };
};

export const getTransformTargetBoundingBox = (target: TransformTarget, applyMatrix?: boolean) => {
  const { targetGroup } = target;
  if (applyMatrix) {
    return computeBoundingBox(targetGroup);
  }
  return getObjectBoundingBox(targetGroup);
};

export const metersToInches = (meters: number) => {
  return meters * METERS_TO_INCHES_FACTOR;
};
export const metersToFeet = (meters: number) => {
  return meters * METERS_TO_FEET_FACTOR;
};

export const lengthToLocaleString = (
  lengthMeters: number,
  unitSystem: UnitSystemEnum,
  options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
) => {
  switch (unitSystem) {
    default:
    case UnitSystemEnum.METRIC:
      return `${lengthMeters.toLocaleString(
        undefined,
        options ?? { minimumFractionDigits: 1, maximumFractionDigits: 1 }
      )}m`;
    case UnitSystemEnum.IMPERIAL:
      return `${metersToFeet(lengthMeters).toLocaleString(
        undefined,
        options ?? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
      )}'`;
  }
};

export const clamp = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

export const abyssRotationFromViewerRotation = (rotation: Number3): Number3 => {
  // abyss rotation has extrinsic order 'xyz', it's same as intrinsic euler order 'ZYX'
  // viewer rotation has intrinsic euler order 'XYZ' (default)
  const abyssEuler = new THREE.Euler().setFromQuaternion(
    new THREE.Quaternion().setFromEuler(new THREE.Euler(...rotation)),
    'ZYX'
  );
  return abyssEuler.toArray().slice(0, 3) as Number3;
};

export const viewerRotationFromAbyssRotation = (abyssRotation: Number3): Number3 => {
  const abyssEuler = new THREE.Euler().setFromQuaternion(
    new THREE.Quaternion().setFromEuler(new THREE.Euler(...abyssRotation, 'ZYX'))
  );
  return abyssEuler.toArray().slice(0, 3) as Number3;
};

export const cameraQuaternionFromAbyssRotation = (rotation: Number3): THREE.Quaternion => {
  const quat2 = new THREE.Quaternion();
  const euler2 = new THREE.Euler(Math.PI / 2, 0, 0, 'XYZ');
  quat2.setFromEuler(euler2);

  const euler = new THREE.Euler(...rotation, 'ZYX');
  const quat = new THREE.Quaternion();
  quat.setFromEuler(euler);

  quat.multiply(quat2);

  return quat;
};

export const cameraEulerFromQuaternion = (quat: THREE.Quaternion): THREE.Euler => {
  const euler = new THREE.Euler();
  euler.setFromQuaternion(quat, 'XYZ');
  return euler;
};

export const cameraEulerFromAbyssRotation = (rotation: Number3): THREE.Euler => {
  const quat = cameraQuaternionFromAbyssRotation(rotation);
  return cameraEulerFromQuaternion(quat);
};

export const cameraDirectionFromQuaternion = (quat: THREE.Quaternion): THREE.Vector3 => {
  const direction = new THREE.Vector3(0, 0, -1);
  direction.applyQuaternion(quat);
  return direction;
};

export const cameraDirectionFromAbyssRotation = (rotation: Number3): THREE.Vector3 => {
  const quat = cameraQuaternionFromAbyssRotation(rotation);
  return cameraDirectionFromQuaternion(quat);
};

export const fovFromCalibration = (focalLengthPixels: number, imageHeight: number) => {
  return THREE.MathUtils.radToDeg(2 * Math.atan(imageHeight / (2 * focalLengthPixels)));
};

export const getCurrentTransformMatrix = (target: TransformTarget): Matrix4 => {
  const { leafGroup } = target;
  leafGroup.updateMatrixWorld(true);
  return leafGroup.matrixWorld.toArray();
};

// returns transform matrix4 of controlled object before reset (to be applied via object props)
export const resetTransformControls = (target: TransformTarget): Matrix4 => {
  const matrix4 = getCurrentTransformMatrix(target);
  // console.log('resetTransformControls', {
  //   matrix4: displayDecomposedMatrix(matrix4),
  // });

  return matrix4;
};

export const positionScaleFromBox = (box: BoxProps): { position: Number3; scale: Number3 } => {
  const [x0, y0, z0] = box.min;
  const [x1, y1, z1] = box.max;
  const position: Number3 = [(x1 + x0) * 0.5, (y1 + y0) * 0.5, (z1 + z0) * 0.5];
  const scale: Number3 = [x1 - x0, y1 - y0, z1 - z0];
  return { position, scale };
};

export const boxFromPositionScale = (position: Number3, scale: Number3): BoxProps => {
  const [cx, cy, cz] = position;
  const [sx, sy, sz] = scale;
  const box: BoxProps = {
    min: [cx - 0.5 * sx, cy - 0.5 * sy, cz - 0.5 * sz],
    max: [cx + 0.5 * sx, cy + 0.5 * sy, cz + 0.5 * sz],
  };
  return box;
};

export const matrixFromCuboid = (cuboid: Transform): THREE.Matrix4 => {
  const { position, rotation, scale } = cuboid;
  const euler0 = new THREE.Euler(...rotation);
  const quat0 = new THREE.Quaternion().setFromEuler(euler0);
  const position0 = new THREE.Vector3(...position);
  const scale0 = new THREE.Vector3(...scale);

  return new THREE.Matrix4().compose(position0, quat0, scale0);
};

export const isPointInsideCuboidMatrix = (
  point: THREE.Vector3,
  matrixInverse: THREE.Matrix4
): boolean => {
  point.applyMatrix4(matrixInverse);
  return UNITARY_CUBOID.containsPoint(point);
};

// returns oredered list of indexes of points, which are inside cuboid
export const findPointsInsideCuboid = (
  cuboid: {
    position: Number3;
    rotation: Number3;
    scale: Number3;
  },
  points: Number3[]
): number[] => {
  const matrixInverse = matrixFromCuboid(cuboid).invert();
  const pointVec = new THREE.Vector3();
  return points
    .map((point, index) => {
      pointVec.set(...point);
      return isPointInsideCuboidMatrix(pointVec, matrixInverse) ? index : -1;
    })
    .filter((pointIndex) => pointIndex !== -1);
};

/**
 * Removes `null` or `undefined` from a value, ensuring the type is narrowed to either the original type `T` or `undefined`.
 *
 * @template T - The type of the value being processed.
 * @param {T | null | undefined} value - The value to process, which may be `null` or `undefined`.
 * @returns {T | undefined} The original value if it is not `null` or `undefined`, otherwise `undefined`.
 *
 * @example
 * const result = removeNullTyping<string>("hello"); // "hello"
 * const result2 = removeNullTyping<string>(null); // undefined
 * const result3 = removeNullTyping<number | undefined>(42); // 42
 * const result4 = removeNullTyping<number | undefined>(undefined); // undefined
 */
export const removeNullTyping = <T>(value: T | null | undefined): T | undefined => {
  if (!value) return value as undefined;
  return value as T;
};

export const calculateCuboidSurfaceArea = (cuboidScale: Number3) => {
  // cuboid is a scaled 1x1x1 cube
  const [sx, sy, sz] = cuboidScale;

  // Surface area calculation
  const surfaceArea = 2 * (sx * sy + sx * sz + sy * sz);

  return surfaceArea;
};

export const calcCameraFovFromFocalLength = (focalLength: number, imageHeight: number): number => {
  // image.focalLength in pixels
  const fov = THREE.MathUtils.radToDeg(2 * Math.atan(imageHeight / (2 * focalLength)));
  return fov;
};

export const directionArrowFromXY = (x: number, y: number): DirectionArrow => {
  //    y
  // -x . x
  //   -y
  const absX = Math.abs(x);
  const absY = Math.abs(y);

  if (absX > absY) {
    return x < 0 ? DirectionArrow.Left : DirectionArrow.Right;
  }
  return y < 0 ? DirectionArrow.Down : DirectionArrow.Up;
};

export const cameraPoseArray = (camera: THREE.Camera) => {
  const fov = camera instanceof THREE.PerspectiveCamera ? camera.fov : 50;
  const zoom = camera instanceof THREE.OrthographicCamera ? camera.zoom : 1;
  const { position, rotation } = camera;
  return [position.x, position.y, position.z, rotation.x, rotation.y, rotation.z, fov, zoom];
};

export const isSameNumber3 = (a: Number3, b: Number3): boolean => {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
};

export const isSameTransform = (a: Transform, b: Transform): boolean => {
  return (
    isSameNumber3(a.position, b.position) &&
    isSameNumber3(a.rotation, b.rotation) &&
    isSameNumber3(a.scale, b.scale)
  );
};
